import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  AutoAwesome as SparkleIcon,
  PlaylistAdd as AddToProjectIcon,
} from '@mui/icons-material';
import { personaService, projectService } from '../services/api';
import type { Persona } from '@personawiz/shared';

interface LocationState {
  projectId?: string;
  projectName?: string;
}

export default function PersonaList() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  
  // Check if we're in "add to project" mode
  const projectId = state?.projectId;
  const projectName = state?.projectName;
  const isAddToProjectMode = !!projectId;
  
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [addingPersonaId, setAddingPersonaId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState('');

  const loadPersonas = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = search ? { search } : {};
      const response = await personaService.list(params);
      setPersonas(response.data);
    } catch (err) {
      setError('Failed to load personas. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadPersonas();
    }, 0);

    return () => clearTimeout(timer);
  }, [loadPersonas]);

  const handlePersonaClick = (personaId: string) => {
    navigate(`/personas/${personaId}`);
  };

  const handleCreateNew = () => {
    // Pass project context if in add-to-project mode
    if (isAddToProjectMode) {
      navigate('/personas/new', { state: { projectId, projectName } });
    } else {
      navigate('/personas/new');
    }
  };

  const handleAddToProject = async (personaId: string) => {
    if (!projectId) return;
    
    setAddingPersonaId(personaId);
    try {
      await projectService.addPersona(projectId, personaId);
      setSnackbar(`Persona added to "${projectName}"`);
      // Navigate back to project after short delay
      setTimeout(() => navigate(`/projects/${projectId}`), 1500);
    } catch (err) {
      setError('Failed to add persona to project. It may already be in the project.');
    } finally {
      setAddingPersonaId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      {/* Project Context Banner */}
      {isAddToProjectMode && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/projects/${projectId}`)}>
              Back to Project
            </Button>
          }
        >
          Select a persona to add to project: <strong>{projectName}</strong>
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {isAddToProjectMode ? 'Add Persona to Project' : 'Personas'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isAddToProjectMode 
              ? 'Click a persona to add it to your project, or generate a new one'
              : 'View and manage all your generated personas'
            }
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          size="large"
        >
          Generate Persona
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search personas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
          size="small"
        />
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!isLoading && personas.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No personas yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate your first persona to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<SparkleIcon />}
            onClick={handleCreateNew}
          >
            Generate Persona
          </Button>
        </Card>
      )}

      {/* Personas Grid */}
      {!isLoading && personas.length > 0 && (
        <Grid container spacing={3}>
          {personas.map((persona) => (
            <Grid item xs={12} sm={6} md={4} key={persona.id}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => isAddToProjectMode ? handleAddToProject(persona.id) : handlePersonaClick(persona.id)}
                  disabled={addingPersonaId !== null}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight={600} noWrap>
                        {persona.coreIdentity?.personaName || 'Unnamed Persona'}
                      </Typography>
                    </Box>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {persona.coreIdentity?.userRole}
                    </Typography>

                    {persona.coreIdentity?.useCase && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontStyle: 'italic',
                        }}
                      >
                        {persona.coreIdentity.useCase}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                      {persona.coreIdentity?.northStarMetric && (
                        <Chip
                          label={persona.coreIdentity.northStarMetric}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ maxWidth: 200 }}
                        />
                      )}
                      {persona.cognitiveProfile?.thinkingStyle && (
                        <Chip
                          label={persona.cognitiveProfile.thinkingStyle}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                      Created {formatDate(persona.createdAt)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
                
                {/* Add to Project button when in add-to-project mode */}
                {isAddToProjectMode && (
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={addingPersonaId === persona.id ? <CircularProgress size={16} /> : <AddToProjectIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToProject(persona.id);
                      }}
                      disabled={addingPersonaId !== null}
                    >
                      {addingPersonaId === persona.id ? 'Adding...' : 'Add to Project'}
                    </Button>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Success Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
